import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import FiscalService from "../../api/fiscal";

import { searchCep } from "../../extras";
import {
  validateArray,
  validateEmail,
  validateOnlyNumbersCNPJ,
  validatePhone,
  validateString,
} from "../../common/validators";
import {
  DOCUMENT_HELPER,
  EMAIL_VALIDATOR_HELPER,
  ERROR_MESSAGE_SUPPORT,
  ERROR_NFCE_CSC,
  ERROR_NFCE_CSC_ID,
  NFCE_CSC_HELPER,
  NFCE_CSC_ID_HELPER,
  STRING_NOT_NULL_HELPER,
} from "../../common/helpersTxt";

// FRAMES
import { taxRegimeFrame } from "../../common/frames";
// MATERIAL UI COMPONENTS
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddressForm from "../address/AddressForm";
import Autocomplete from "@mui/material/Autocomplete";
import { DarkGreyBox, LightDarkGreyBox, LightGreyBox } from "../styled";
// ICONS
import CloseIcon from "@mui/icons-material/Close";
// COLORS
import { grey } from "@mui/material/colors";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { ButtonSuccessNoShadow } from "../styled/button";
import { cnpjMask, cpfMask } from "../../utils/formatters";

export default function FiscalDataForm({ fiscalId, address, isMerchant }) {
  const [loading, setLoading] = useState(true);

  const [companyId, setCompanyId] = useState(null);

  const [identificationInputValue, setIdentificationInputValue] = useState("");
  const [identificationError, setIdentificationError] = useState(false);

  const [nameInputValue, setNameInputValue] = useState("");
  const [nameInputError, setNameInputError] = useState(false);

  const [fantasyNameInputValue, setFantasyNameInputValue] = useState("");
  const [fantasyNameError, setFantasyNameError] = useState(false);

  const [stateRegistrationInputValue, setStateRegistrationInputValue] =
    useState("");
  const [stateRegistrationError, setStateRegistrationError] = useState(false);

  const [cityRegistrationInputValue, setCityRegistrationInputValue] =
    useState("");
  const [cityRegistrationError, setCityRegistrationError] = useState(false);

  const [companyPhoneInputValue, setCompanyPhoneInputValue] = useState("");
  const [companyPhoneError, setCompanyPhoneError] = useState(false);

  const [companyEmailInputValue, setCompanyEmailInputValue] = useState("");
  const [companyEmailError, setCompanyEmailError] = useState(false);

  const [taxRegime, setTaxRegime] = useState(null);

  const [nfceCSCInputValue, setNfceCSCInputValue] = useState("");
  const [nfceCSCError, setNfceCSCError] = useState(false);

  const [nfceCSCIdInputValue, setNfceCSCIdInputValue] = useState("");
  const [nfceCSCIdError, setNfceCSCIdError] = useState(false);

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [base64Data, setBase64Data] = useState(null);

  const [certificatePasswordInputValue, setCertificatePasswordInputValue] =
    useState("");
  const [certificatePasswordError, setCertificatePasswordError] =
    useState(false);

  const [useDeliveryCenterAddress, setUseDeliveryCenterAddress] =
    useState(true);

  const [addressId, setAddressId] = useState(address?.addressId || uuidv4());
  const [cep, setCep] = useState(address?.cep || "");
  const [street, setStreet] = useState(address?.street || "");
  const [houseNumber, setHouseNumber] = useState(address?.houseNumber || "");
  const [neighborhood, setNeighborhood] = useState(address?.neighborhood || "");
  const [complement, setComplement] = useState(address?.complement || "");
  const [reference, setReference] = useState(address?.reference || "");
  const [city, setCity] = useState(address?.city || "");
  const [uf, setUf] = useState(address?.state || "");

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      if (fiscalId) {
        const { data } = await FiscalService.getFiscalInfo(fiscalId);

        if (!data) return;

        setUseDeliveryCenterAddress(false);

        const { address_info, fiscal_info } = data;

        setCompanyId(fiscal_info.focus_nfe_company_id);

        setIdentificationInputValue(fiscal_info.cnpj);
        setNameInputValue(fiscal_info.name);
        setFantasyNameInputValue(fiscal_info.fantasy_name);
        setStateRegistrationInputValue(fiscal_info.state_registration);
        setCityRegistrationInputValue(fiscal_info.city_registration);
        setCompanyPhoneInputValue(fiscal_info.phone);
        setCompanyEmailInputValue(fiscal_info.email);

        const filterTaxRegime = taxRegimeFrame.find(
          (taxRegime) => taxRegime.value === fiscal_info.tax_regime
        );
        setTaxRegime(filterTaxRegime);

        setNfceCSCInputValue(fiscal_info.nfce_csc_production);
        setNfceCSCIdInputValue(fiscal_info.nfce_csc_id_production);
        setCertificatePasswordInputValue(fiscal_info.certificate_password);
        setBase64Data(fiscal_info.base64_certificate_file);

        setAddressId(address_info.address_id);
        setCep(address_info.cep);
        setStreet(address_info.street);
        setHouseNumber(address_info.house_number);
        setNeighborhood(address_info.neighborhood);
        setComplement(address_info.address_complement);
        setReference(address_info.address_reference);
        setCity(address_info.city);
        setUf(address_info.uf);
      }
    } catch (e) {
      setErrorMessage(e);
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const _searchCep = async () => {
    if (cep.length === 8) {
      const data = await searchCep(cep);

      setStreet(data.logradouro);
      setNeighborhood(data.bairro);
      setCity(data.localidade);
      setUf(data.uf);
    }
  };

  const handleUseDeliveryCenterAddressChange = (e) => {
    setUseDeliveryCenterAddress(e.target.checked);
  };

  const handleNameInputChange = (event) => {
    const value = event.target.value;
    setNameInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setNameInputError(true);
    } else {
      setNameInputError(false);
    }
  };

  const handleFantasyNameInputChange = (event) => {
    const value = event.target.value;
    setFantasyNameInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setFantasyNameError(true);
    } else {
      setFantasyNameError(false);
    }
  };

  const handleStateRegistrationInputChange = (event) => {
    const value = event.target.value;
    setStateRegistrationInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setStateRegistrationError(true);
    } else {
      setStateRegistrationError(false);
    }
  };

  const handleCityRegistrationInputChange = (event) => {
    const value = event.target.value;
    setCityRegistrationInputValue(value);
  };

  const handleCompanyPhoneInputChange = (event) => {
    const value = event.target.value;
    setCompanyPhoneInputValue(value);

    const isValid = validatePhone(value);
    if (!isValid) {
      setCompanyPhoneError(true);
    } else {
      setCompanyPhoneError(false);
    }
  };

  const handleCompanyEmailInputChange = (event) => {
    const value = event.target.value;
    setCompanyEmailInputValue(value);

    const isValid = validateEmail(value);
    if (!isValid) {
      setCompanyEmailError(true);
    } else {
      setCompanyEmailError(false);
    }
  };

  const handleNfceCSCInputChange = (event) => {
    const value = event.target.value;
    setNfceCSCInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setNfceCSCError(true);
    } else {
      setNfceCSCError(false);
    }
  };

  const handleNfceCSCIdInputChange = (event) => {
    const value = event.target.value;
    setNfceCSCIdInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setNfceCSCIdError(true);
    } else {
      setNfceCSCIdError(false);
    }
  };

  const handleCertificatePasswordInputChange = (event) => {
    const value = event.target.value;
    setCertificatePasswordInputValue(value);

    const isValid = validateString(value);
    if (!isValid) {
      setCertificatePasswordError(true);
    } else {
      setCertificatePasswordError(false);
    }
  };

  const handleIdentificationInputChange = (event) => {
    const value = event.target.value;
    const formattedValue = applyMask(value);
    setIdentificationInputValue(formattedValue);

    const isValid = validateOnlyNumbersCNPJ(value);
    if (!isValid) {
      setIdentificationError(true);
    } else {
      setIdentificationError(false);
    }
  };

  const applyMask = (value) => {
    const onlyNumbers = value.replace(/\D/g, "");
    if (onlyNumbers.length <= 11) {
      return cpfMask(onlyNumbers);
    } else {
      return cnpjMask(onlyNumbers);
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];
      setBase64Data(base64String);
    };

    reader.readAsDataURL(file);
  };

  const handleCustomButtonClick = () => {
    fileInputRef.current.click();
  };

  const formatSize = (size) => {
    const sizeInMB = size / (1024 * 1024);
    return `${sizeInMB.toFixed(2)} MB`;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const valuesToValidate = [
        identificationInputValue,
        nameInputValue,
        fantasyNameInputValue,
        stateRegistrationInputValue,
        companyPhoneInputValue,
        companyEmailInputValue,
        taxRegime,
        nfceCSCInputValue,
        nfceCSCIdInputValue,
        base64Data,
        certificatePasswordInputValue,
        cep,
        street,
        houseNumber,
        neighborhood,
        city,
        uf,
      ];

      const isValid = validateArray(valuesToValidate);

      if (!isValid) {
        setIdentificationError(!identificationInputValue);
        setNameInputError(!nameInputValue);
        setFantasyNameError(!fantasyNameInputValue);
        setStateRegistrationError(!stateRegistrationInputValue);
        setCompanyPhoneError(!companyPhoneInputValue);
        setCompanyEmailError(!companyEmailInputValue);
        setNfceCSCError(!nfceCSCInputValue);
        setNfceCSCIdError(!nfceCSCIdInputValue);
        setCertificatePasswordError(!certificatePasswordInputValue);

        setOpenErrorAlert(true);
        setErrorMessage("Preencha todos os campos");
        return;
      }

      if (useDeliveryCenterAddress) {
        setCep(address?.cep || "");
        setStreet(address?.street || "");
        setHouseNumber(address?.houseNumber || "");
        setNeighborhood(address?.neighborhood || "");
        setComplement(address?.complement || "");
        setReference(address?.reference || "");
        setCity(address?.city || "");
        setUf(address?.state || "");
      }

      const companyObj = {
        id: companyId,
        nome: nameInputValue,
        nome_fantasia: fantasyNameInputValue,
        cnpj: Number(identificationInputValue.replace(/\D/g, "")),
        inscricao_estadual: Number(stateRegistrationInputValue),
        inscricao_municipal: Number(cityRegistrationInputValue),
        telefone: Number(companyPhoneInputValue),
        email: companyEmailInputValue,
        cep: Number(cep),
        bairro: neighborhood,
        logradouro: street,
        numero: houseNumber,
        complemento: complement,
        municipio: city,
        uf: uf,
        discrimina_impostos: true,
        enviar_email_destinatario: true,
        habilita_nfe: true,
        habilita_nfce: true,
        arquivo_certificado_base64: base64Data,
        senha_certificado: certificatePasswordInputValue,
        regime_tributario: taxRegime.value,
        csc_nfce_producao: nfceCSCInputValue,
        id_token_nfce_producao: nfceCSCIdInputValue,
      };

      if (fiscalId) {
        const putOnlyParams = {
          fk_address: addressId,
          certificate_password: certificatePasswordInputValue,
          city_registration: Number(cityRegistrationInputValue),
          fantasy_name: fantasyNameInputValue,
          name: nameInputValue,
          phone: Number(companyPhoneInputValue),
          state_registration: Number(stateRegistrationInputValue),
          tax_regime: taxRegime.value,
          base64_certificate_file: base64Data,
        };

        if (isMerchant) {
          await FiscalService.alterMerchantCompany(companyId, {
            ...companyObj,
            ...putOnlyParams,
          });
        } else {
          await FiscalService.alterCompany(companyId, {
            ...companyObj,
            ...putOnlyParams,
          });
        }
      } else {
        if (isMerchant) {
          await FiscalService.registerMerchantCompany(companyObj);
        } else {
          await FiscalService.registerCompany(companyObj);
        }
      }
    } catch (error) {
      setErrorMessage(
        error.response
          ? error.response.data.error || ERROR_MESSAGE_SUPPORT
          : error.message || ERROR_MESSAGE_SUPPORT
      );
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <Stack
          spacing={2}
          sx={{
            m: "auto",
          }}
        >
          <CircularProgress sx={{ "&:focus-visible": { border: "none" } }} />
        </Stack>
      </Box>
    );
  }

  return (
    <LightGreyBox>
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Divider
              sx={{ p: 2, backgroundColor: grey["200"], borderRadius: 5 }}
            >
              <Chip label="DADOS GERAIS DA EMPRESA" />
            </Divider>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="fiscal-name-input"
              label="Nome da Empresa"
              variant="outlined"
              value={nameInputValue}
              onChange={handleNameInputChange}
              error={nameInputError}
              helperText={nameInputError ? STRING_NOT_NULL_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="fiscal-fantasy-name-input"
              label="Nome Fantasia"
              variant="outlined"
              value={fantasyNameInputValue}
              onChange={handleFantasyNameInputChange}
              error={fantasyNameError}
              helperText={fantasyNameError ? STRING_NOT_NULL_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="fiscal-state-registration-input"
              label="Inscrição Estadual"
              variant="outlined"
              autoComplete="state-registration"
              value={stateRegistrationInputValue}
              onChange={handleStateRegistrationInputChange}
              error={stateRegistrationError}
              helperText={stateRegistrationError ? STRING_NOT_NULL_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="fiscal-citying-registration-input"
              label="Inscrição Municipal"
              variant="outlined"
              autoComplete="city-registration"
              value={cityRegistrationInputValue}
              onChange={handleCityRegistrationInputChange}
              error={cityRegistrationError}
              helperText={cityRegistrationError ? STRING_NOT_NULL_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="company-phone-input"
              label="Telefone"
              variant="outlined"
              value={companyPhoneInputValue}
              onChange={handleCompanyPhoneInputChange}
              error={companyPhoneError}
              helperText={companyPhoneError ? STRING_NOT_NULL_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="company-email-input"
              label="E-mail"
              variant="outlined"
              value={companyEmailInputValue}
              onChange={handleCompanyEmailInputChange}
              error={companyEmailError}
              helperText={companyEmailError ? EMAIL_VALIDATOR_HELPER : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              id="fiscal-cpf-cnpj-input"
              label="CNPJ"
              variant="outlined"
              value={identificationInputValue}
              onChange={handleIdentificationInputChange}
              error={identificationError}
              helperText={identificationError ? DOCUMENT_HELPER : ""}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ p: 2, backgroundColor: grey["200"], borderRadius: 5 }}>
          <Chip label="ENDEREÇO" />
        </Divider>

        <LightDarkGreyBox display="flex" justifyContent="center">
          <Typography textAlign="center" sx={{ p: 2, mb: 0 }}>
            Usar endereço da operação:
          </Typography>

          <Checkbox
            checked={useDeliveryCenterAddress}
            onChange={handleUseDeliveryCenterAddressChange}
            inputProps={{ "aria-label": "controlled" }}
          />
        </LightDarkGreyBox>

        {!useDeliveryCenterAddress && (
          <AddressForm
            addressId={addressId}
            setAddressId={setAddressId}
            cep={cep}
            setCep={setCep}
            street={street}
            setStreet={setStreet}
            houseNumber={houseNumber}
            setHouseNumber={setHouseNumber}
            neighborhood={neighborhood}
            setNeighborhood={setNeighborhood}
            complement={complement}
            setComplement={setComplement}
            reference={reference}
            setReference={setReference}
            city={city}
            setCity={setCity}
            uf={uf}
            setUf={setUf}
            searchCep={_searchCep}
          />
        )}

        <Divider sx={{ p: 2, backgroundColor: grey["200"], borderRadius: 5 }}>
          <Chip label="DADOS FISCAIS" />
        </Divider>

        <Autocomplete
          size="small"
          fullWidth
          value={taxRegime}
          onChange={(event, newValue) => {
            setTaxRegime(newValue);
          }}
          options={taxRegimeFrame}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Regime Tributário"
              variant="outlined"
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <TextField
            id="nfce-csc-input"
            label="Token CSC"
            variant="outlined"
            size="small"
            value={nfceCSCInputValue}
            onChange={handleNfceCSCInputChange}
            error={nfceCSCError}
            helperText={nfceCSCError ? ERROR_NFCE_CSC : NFCE_CSC_HELPER}
          />
          <TextField
            id="nfce-csc-id-input"
            label="ID do CSC"
            variant="outlined"
            size="small"
            value={nfceCSCIdInputValue}
            onChange={handleNfceCSCIdInputChange}
            error={nfceCSCIdError}
            helperText={nfceCSCIdError ? ERROR_NFCE_CSC_ID : NFCE_CSC_ID_HELPER}
          />
        </Stack>

        <Divider sx={{ p: 2, backgroundColor: grey["200"], borderRadius: 5 }}>
          <Chip label="CERTIFICADO" />
        </Divider>

        <DarkGreyBox display="center" justifyContent="center">
          <input
            accept="*"
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />
          <Stack spacing={1}>
            {(selectedFile || base64Data) && (
              <LightDarkGreyBox>
                {selectedFile ? (
                  <Typography>
                    {selectedFile.name}
                    {" - "}
                    {formatSize(selectedFile.size)}
                  </Typography>
                ) : (
                  <Typography textAlign="center" color="green">
                    Arquivo Já Enviado
                  </Typography>
                )}
              </LightDarkGreyBox>
            )}
            <LightDarkGreyBox sx={{ mb: 2 }}>
              <TextField
                id="fiscal-certificate-password-input"
                label="Senha do Certificado"
                variant="outlined"
                size="small"
                value={certificatePasswordInputValue}
                onChange={handleCertificatePasswordInputChange}
                error={certificatePasswordError}
                helperText={
                  certificatePasswordError ? STRING_NOT_NULL_HELPER : ""
                }
                fullWidth
              />
            </LightDarkGreyBox>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCustomButtonClick}
            >
              Selecionar Arquivo Certificado
            </Button>
          </Stack>
        </DarkGreyBox>

        <ButtonSuccessNoShadow onClick={handleSubmit} sx={{ mb: 2 }}>
          SALVAR
        </ButtonSuccessNoShadow>

        <Collapse in={openErrorAlert}>
          <Alert
            variant="filled"
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpenErrorAlert(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>
      </Stack>
    </LightGreyBox>
  );
}
